import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { decideI18nDrafts, listI18nDrafts } from "@/lib/i18n-drafts.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/**
 * Admin-only scherm (NL). Bewust geen i18n-namespace: dit is een intern
 * beheertool, geen publieke UI.
 */
const L = {
  heading: "Draft-vertalingen",
  sub: "Automatisch gegenereerde vertalingen. Keur goed of wijs af voor ze live gaan.",
  empty: "Geen drafts open. Alles is nagekeken.",
  loading: "Laden…",
  filterLang: "Alle talen",
  search: "Zoek op key of tekst…",
  source: "Bron (nl)",
  translation: "Vertaling",
  approve: "Goedkeuren",
  reject: "Afwijzen",
  approveAll: "Alle zichtbare goedkeuren",
  saved: "Beslissing opgeslagen.",
  failed: "Dat lukte niet. Probeer opnieuw.",
  recent: "Recent beslist",
  loadError: "Kon de drafts niet laden.",
  count: "open draft(s)",
} as const;

export const Route = createFileRoute("/_authenticated/admin/i18n")({
  head: () => ({
    meta: [
      { title: "Vertaal-drafts | Beheer IT Solutions" },
      {
        name: "description",
        content: "Beheer en beoordeel automatisch gegenereerde EN/FR-vertalingen.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Vertaal-drafts | Beheer IT Solutions" },
      {
        property: "og:description",
        content: "Beheer en beoordeel automatisch gegenereerde EN/FR-vertalingen.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: I18nDraftsPage,
});

function I18nDraftsPage() {
  const list = useServerFn(listI18nDrafts);
  const decide = useServerFn(decideI18nDrafts);
  const qc = useQueryClient();

  const [lang, setLang] = useState<string>("");
  const [q, setQ] = useState("");
  const [edits, setEdits] = useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["i18n-drafts"],
    queryFn: () => list(),
  });

  const mutation = useMutation({
    mutationFn: (decisions: Parameters<typeof decide>[0] extends never ? never : any) =>
      decide({ data: { decisions } }),
    onSuccess: () => {
      toast.success(L.saved);
      setEdits({});
      void qc.invalidateQueries({ queryKey: ["i18n-drafts"] });
    },
    onError: () => toast.error(L.failed),
  });

  const drafts = data?.drafts ?? [];
  const langs = useMemo(() => [...new Set(drafts.map((d) => d.lang))].sort(), [drafts]);

  const visible = useMemo(
    () =>
      drafts.filter((d) => {
        if (lang && d.lang !== lang) return false;
        if (!q.trim()) return true;
        const needle = q.toLowerCase();
        return (
          d.key.toLowerCase().includes(needle) ||
          d.ns.toLowerCase().includes(needle) ||
          d.source.toLowerCase().includes(needle) ||
          d.value.toLowerCase().includes(needle)
        );
      }),
    [drafts, lang, q],
  );

  const idOf = (d: { lang: string; ns: string; key: string }) => `${d.lang}|${d.ns}|${d.key}`;

  const submit = (
    items: { lang: string; ns: string; key: string }[],
    action: "approve" | "reject",
  ) =>
    mutation.mutate(
      items.map((d) => ({
        lang: d.lang,
        ns: d.ns,
        key: d.key,
        action,
        value: action === "approve" ? edits[idOf(d)] : undefined,
      })) as never,
    );

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">{L.heading}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{L.sub}</p>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground"
        >
          <option value="">{L.filterLang}</option>
          {langs.map((l) => (
            <option key={l} value={l}>
              {l.toUpperCase()}
            </option>
          ))}
        </select>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={L.search}
          className="max-w-xs"
        />
        <Badge variant="secondary">
          {visible.length} {L.count}
        </Badge>
        {visible.length > 0 && (
          <Button
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => submit(visible, "approve")}
          >
            {L.approveAll}
          </Button>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">{L.loading}</p>}
      {error && <p className="text-sm text-destructive">{L.loadError}</p>}
      {!isLoading && !error && visible.length === 0 && (
        <p className="text-sm text-muted-foreground">{L.empty}</p>
      )}

      <ul className="space-y-4">
        {visible.map((d) => {
          const id = idOf(d);
          return (
            <li key={id} className="rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge>{d.lang.toUpperCase()}</Badge>
                <Badge variant="outline">{d.ns}</Badge>
                <code className="text-xs text-muted-foreground">{d.key}</code>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs font-medium text-muted-foreground">{L.source}</p>
                  <p className="rounded-md bg-muted/50 p-2 text-sm text-foreground">{d.source}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium text-muted-foreground">{L.translation}</p>
                  <Textarea
                    value={edits[id] ?? d.value}
                    onChange={(e) => setEdits((s) => ({ ...s, [id]: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  disabled={mutation.isPending}
                  onClick={() => submit([d], "approve")}
                >
                  {L.approve}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={mutation.isPending}
                  onClick={() => submit([d], "reject")}
                >
                  {L.reject}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {data?.recent && data.recent.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{L.recent}</h2>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {data.recent.slice(0, 20).map((h, i) => (
              <li key={`${h.lang}-${h.ns}-${h.key}-${i}`}>
                <span className="uppercase">{h.lang}</span> · {h.ns}.{h.key} ·{" "}
                <span className={h.status === "approved" ? "text-primary" : "text-destructive"}>
                  {h.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
